import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { OCViewer } from '../../components/OCViewer';
import { VbPagoBadge } from '../../components/VbPagoBadge';
import { useEmpresa } from '../../hooks/useEmpresa';
import { TableSkeleton } from '../../lib/skeletons';
import { OCNumber } from '../../lib/ocFormat';
import { fmtFecha } from '../../lib/utils'; // Iter 292.h — formato canónico DD-MM-YYYY
import {
  Search,
  RefreshCw,
  Eye,
  Printer,
  Package,
  ChevronLeft,
  ChevronRight,
  FileText,
  ScanLine,
  CheckCircle,
  Clock,
  Loader2,
  Filter,
  ClipboardList,
  AlertTriangle,
  CloudDownload,
  Database,
  Globe,
  FileSpreadsheet,
  Ban,
  Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { exportToCSV, printTable } from '../../lib/printUtils';

const fmt = (n) => typeof n === 'number' ? n.toLocaleString('es-CL', { maximumFractionDigits: 0 }) : (n || '0');

// Parse Access date format "MM/DD/YY 0" or ISO or standard date strings
const parseDate = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim().replace(/\s+0$/, ''); // remove trailing " 0"
  // Try MM/DD/YY format
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (m) {
    let y = parseInt(m[3], 10);
    if (y < 100) y += y > 50 ? 1900 : 2000;
    return new Date(y, parseInt(m[1], 10) - 1, parseInt(m[2], 10));
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const fmtDate = (raw) => {
  // Iter 292.h — usa fmtFecha canónico (DD-MM-YYYY).
  return fmtFecha(raw) || '-';
};

const ESTADOS_OC = [
  { value: '', label: 'Todos' },
  { value: 'aprobada', label: 'Aprobadas' },
  { value: 'pendiente', label: 'Pendientes' },
];

const ESTADOS_CONTABLE = [
  { value: '', label: 'Todos' },
  { value: 'total', label: 'Contabilizada' },
  { value: 'parcial', label: 'Parcial' },
  { value: 'excede', label: 'Excede OC' },
  { value: 'pendiente', label: 'Sin Contabilizar' },
];

export default function SeguimientoOC() {
  const empresa = useEmpresa();
  const { user } = useAuth();
  const role = (user?.rol || user?.role || '').toLowerCase();
  const canManageRecepciones = ['admin', 'administrador', 'gerencia', 'bodega_admin', 'jefe_bodega'].includes(role);

  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [estadoContableFilter, setEstadoContableFilter] = useState('');
  const [fechaDesde, setFechaDesde] = useState('2026-01-01');
  const [fechaHasta, setFechaHasta] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [contabStats, setContabStats] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const pageSize = 50;

  // OC detail dialog
  const [ocDetail, setOcDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // PDF viewer
  const [pdfView, setPdfView] = useState(null);

  // Iter 220: anular/editar recepción
  const [anularDlg, setAnularDlg] = useState(null); // { numero_recepcion, numero_oc }
  const [anularMotivo, setAnularMotivo] = useState('');
  const [anulando, setAnulando] = useState(false);
  const [editDlg, setEditDlg] = useState(null); // { numero_recepcion, items: [...], numero_oc }
  const [editing, setEditing] = useState(false);

  // Iter 236.m.10 (Carlos): modal "Candidatas a OC recurrente"
  const [recurrentesModal, setRecurrentesModal] = useState(false);
  const [recurrentesLoading, setRecurrentesLoading] = useState(false);
  const [candidatas, setCandidatas] = useState([]);
  const [marcandoRecurrente, setMarcandoRecurrente] = useState(null); // numero_oc en curso

  const openRecurrentesModal = useCallback(async () => {
    setRecurrentesModal(true);
    setRecurrentesLoading(true);
    try {
      const res = await api.get('/compras/ordenes-compra/candidatas-recurrentes');
      setCandidatas(res.data?.candidatas || []);
    } catch (e) {
      toast.error('Error al cargar candidatas: ' + (e?.response?.data?.detail || e?.message || ''));
    } finally {
      setRecurrentesLoading(false);
    }
  }, []);

  const marcarComoRecurrente = useCallback(async (numero_oc) => {
    setMarcandoRecurrente(numero_oc);
    try {
      await api.patch(`/compras/ordenes-compra/${numero_oc}/recurrente`, { es_recurrente: true });
      toast.success(`OC ${numero_oc} marcada como recurrente 🔁`);
      setCandidatas((prev) => prev.filter((c) => c.numero_oc !== numero_oc));
    } catch (e) {
      toast.error('Error: ' + (e?.response?.data?.detail || e?.message || ''));
    } finally {
      setMarcandoRecurrente(null);
    }
  }, []);

  const desmarcarRecurrente = useCallback(async (numero_oc) => {
    try {
      await api.patch(`/compras/ordenes-compra/${numero_oc}/recurrente`, { es_recurrente: false });
      toast.success(`OC ${numero_oc} ya no es recurrente`);
      fetchOrdenes();
    } catch (e) {
      toast.error('Error: ' + (e?.response?.data?.detail || e?.message || ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshDetail = async (numero_oc) => {
    if (!numero_oc) return;
    try {
      // Iter 220: refrescar solo la sub-lista de recepciones de esta OC para
      // reflejar el flag `anulada` o el monto recalculado tras editar.
      const res = await api.get('/compras/recepciones', { params: { numero_oc, limit: 50 } });
      const recepciones = res.data?.recepciones || [];
      setOcDetail((prev) => prev ? { ...prev, oc: { ...prev.oc, recepciones } } : prev);
    } catch { /* silent */ }
  };

  const confirmarAnular = async () => {
    if (!anularDlg) return;
    if ((anularMotivo || '').trim().length < 5) {
      toast.error('El motivo debe tener al menos 5 caracteres');
      return;
    }
    setAnulando(true);
    try {
      const res = await api.post(`/bodega/recepciones/${anularDlg.numero_recepcion}/anular`, {
        motivo: anularMotivo.trim(),
      });
      toast.success(res.data?.message || 'Recepción anulada');
      setAnularDlg(null);
      setAnularMotivo('');
      await refreshDetail(anularDlg.numero_oc);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al anular recepción');
    } finally {
      setAnulando(false);
    }
  };

  const abrirEditarRecepcion = async (numero_recepcion, numero_oc) => {
    try {
      const res = await api.get(`/compras/recepciones/${numero_recepcion}`);
      const items = (res.data?.detalles || []).map((d) => ({
        item: d.item,
        descripcion: d.descripcion || '',
        codigo_material: d.codigo_material || '',
        cantidad_pedida: d.cantidad_pedida || 0,
        cantidad_recepcionada: d.cantidad_recepcionada || 0,
        valor_unitario: d.valor_unitario || 0,
        ot_numero: d.ot_numero || null,
      }));
      setEditDlg({ numero_recepcion, numero_oc, items, motivo: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cargar detalle');
    }
  };

  const confirmarEditar = async () => {
    if (!editDlg) return;
    setEditing(true);
    try {
      const res = await api.put(`/bodega/recepciones/${editDlg.numero_recepcion}`, {
        items: editDlg.items.map((it) => ({
          item: it.item,
          cantidad_recepcionada: Number(it.cantidad_recepcionada) || 0,
          valor_unitario: Number(it.valor_unitario) || 0,
          codigo_material: it.codigo_material || '',
          descripcion: it.descripcion || '',
          ot_numero: it.ot_numero || null,
        })),
        motivo: editDlg.motivo || '',
      });
      toast.success(res.data?.message || 'Recepción editada');
      const ocNum = editDlg.numero_oc;
      setEditDlg(null);
      await refreshDetail(ocNum);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al editar recepción');
    } finally {
      setEditing(false);
    }
  };

  const fetchOrdenes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', page * pageSize);
      params.append('limit', pageSize);
      if (searchTerm) params.append('q', searchTerm);
      if (estadoFilter === 'aprobada') params.append('solo_aprobadas', 'true');
      if (estadoFilter === 'pendiente') params.append('solo_aprobadas', 'false');
      if (estadoContableFilter) params.append('estado_contable', estadoContableFilter);
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      const res = await api.get(`/compras/ordenes-compra?${params.toString()}`);
      setOrdenes(res.data.ordenes_compra || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Error al cargar órdenes');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, estadoFilter, estadoContableFilter, fechaDesde, fechaHasta]);

  useEffect(() => { fetchOrdenes(); }, [fetchOrdenes]);

  // Carlos (10/05/2026): imprimir y exportar Seguimiento OC
  const imprimirSeguimiento = () => {
    if (!ordenes.length) { toast.warning('No hay órdenes para imprimir'); return; }
    printTable({
      title: 'Seguimiento de Órdenes de Compra',
      subtitle: `${ordenes.length} OCs · ${fechaDesde || ''} → ${fechaHasta || 'hoy'} · Página ${page + 1} · ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'OCs visibles', value: ordenes.length },
        { label: 'Total general', value: total },
        { label: 'Filtro estado', value: estadoFilter || 'Todos' },
        { label: 'Estado contable', value: estadoContableFilter || 'Todos' },
      ],
      columns: [
        { key: 'numero_oc', label: 'OC', mono: true, align: 'center' },
        { key: 'fecha_creacion', label: 'Fecha', format: (v) => fmtFecha(v) || '' },
        { key: 'razon_social_proveedor', label: 'Proveedor', format: (v, r) => v || r.rut_proveedor || '' },
        { key: 'numero_documento', label: 'Doc', format: (v) => v || '' },
        { key: 'estado_vb', label: 'VB', align: 'center', format: (v) => (v || 'pend.').toUpperCase() },
        { key: 'estado_recepcion', label: 'Recep.', align: 'center', format: (v) => (v || 'pend.').toUpperCase() },
        { key: 'estado_contable', label: 'Contab.', align: 'center', format: (v) => (v || 'pend.').toUpperCase() },
        { key: 'subtotal_total', label: 'Total', align: 'right', mono: true, format: (v) => `$${fmt(v)}` },
      ],
      rows: ordenes,
    });
  };

  const exportarSeguimientoExcel = () => {
    if (!ordenes.length) { toast.warning('No hay órdenes para exportar'); return; }
    exportToCSV({
      filename: `seguimiento_ocs_pag${page + 1}`,
      title: 'CHIRINO STEEL · Seguimiento de Órdenes de Compra',
      summary: [
        { label: 'OCs en archivo', value: ordenes.length },
        { label: 'Total general', value: total },
        { label: 'Página', value: page + 1 },
        { label: 'Período', value: `${fechaDesde || '-'} → ${fechaHasta || 'hoy'}` },
        { label: 'Filtro VB', value: estadoFilter || 'Todos' },
        { label: 'Filtro contable', value: estadoContableFilter || 'Todos' },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'numero_oc', label: 'N. OC' },
        { key: 'fecha_creacion', label: 'Fecha creación', format: (v) => fmtFecha(v) || '' },
        { key: 'rut_proveedor', label: 'RUT' },
        { key: 'razon_social_proveedor', label: 'Proveedor' },
        { key: 'numero_documento', label: 'N. Documento' },
        { key: 'tipo_documento', label: 'Tipo Doc' },
        { key: 'estado_vb', label: 'Estado VB' },
        { key: 'estado_recepcion', label: 'Estado Recepción' },
        { key: 'estado_contable', label: 'Estado Contable' },
        { key: 'forma_pago', label: 'Forma Pago' },
        { key: 'plazo_pago', label: 'Plazo Pago' },
        { key: 'subtotal_neto', label: 'Total Neto (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'subtotal_iva', label: 'IVA (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'subtotal_total', label: 'Total Bruto (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'creado_por', label: 'Creado por' },
        { key: 'observaciones', label: 'Observaciones' },
      ],
      rows: ordenes,
    });
  };

  const fetchContabStats = useCallback(() => {
    const params = new URLSearchParams();
    if (fechaDesde) params.append('fecha_desde', fechaDesde);
    if (fechaHasta) params.append('fecha_hasta', fechaHasta);
    api.get(`/dashboard/compras?${params.toString()}`)
      .then(res => setContabStats(res.data))
      .catch(() => {});
  }, [fechaDesde, fechaHasta]);

  // Fetch contabilización stats on mount and when dates change
  useEffect(() => { fetchContabStats(); }, [fetchContabStats]);

  const handleSyncAlegra = async () => {
    setSyncing(true);
    toast.info('Sincronizando con Alegra...');
    try {
      await api.post('/compras/sync-contabilizacion');
      // Poll status until done
      const poll = async () => {
        const res = await api.get('/compras/sync-contabilizacion/status');
        if (res.data.running) {
          setTimeout(poll, 2000);
        } else {
          setSyncing(false);
          const result = res.data.last_result;
          if (result && result.nuevos > 0) {
            toast.success(`Sync completada: ${result.nuevos} nuevos registros (${result.total_contabilizadas} total)`);
          } else {
            toast.success('Sync completada: todo al día');
          }
          fetchOrdenes();
          fetchContabStats();
        }
      };
      setTimeout(poll, 3000);
    } catch {
      toast.error('Error al sincronizar con Alegra');
      setSyncing(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchOrdenes();
  };

  // Iter 414 — refrescar el badge de V°B° para Pago en la tabla cuando se
  // otorga/revierte desde el detalle de la OC (sin recargar la página).
  const handleVbPagoChange = useCallback((numeroOc, vb) => {
    const nuevoEstado = vb?.estado;
    if (!numeroOc || !nuevoEstado) return;
    setOrdenes((prev) => prev.map((o) =>
      String(o.numero_oc) === String(numeroOc) ? { ...o, estado_vb_pago: nuevoEstado } : o
    ));
    setOcDetail((prev) =>
      String(prev?.oc?.numero_oc) === String(numeroOc)
        ? { ...prev, oc: { ...prev.oc, estado_vb_pago: nuevoEstado } }
        : prev
    );
  }, []);

  const openOCDetail = async (oc) => {
    setLoadingDetail(true);
    setOcDetail({ oc, loading: true });
    try {
      const res = await api.get(`/compras/orden-compra/${oc.numero_oc}/detalle-completo`);
      // Merge enriched docs from list endpoint (recepciones, facturas_dte) into detail
      const detailOc = { ...res.data.oc, recepciones: oc.recepciones || [], facturas_dte: oc.facturas_dte || [] };
      setOcDetail({ ...res.data, oc: detailOc, loading: false });
    } catch {
      toast.error('Error al cargar detalle de OC');
      setOcDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handlePrint = async (oc) => {
    toast.info('Generando PDF...');
    try {
      const res = await api.get(`/compras/oc/${oc.numero_oc}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (w) {
        w.addEventListener('load', () => { w.print(); });
      }
    } catch {
      toast.error('Error al generar PDF');
    }
  };

  const openPdf = (url, title) => setPdfView({ url: `/api${url}`, title });

  const openScan = async (facturaNum) => {
    if (!facturaNum) { toast.error('Sin factura asociada'); return; }
    toast.info('Buscando scan...');
    try {
      const res = await api.get(`/contabilidad/sharepoint-scan/${facturaNum}`, { responseType: 'blob', validateStatus: s => s < 500 });
      if (res.status >= 400) { toast.error('Scan no disponible'); return; }
      const blob = new Blob([res.data], { type: 'application/pdf' });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch { toast.error('Error al obtener scan'); }
  };

  const totalPages = Math.ceil(total / pageSize);

  // PDF Viewer Dialog component
  const PdfViewerDialog = () => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(true);
    const [pdfError, setPdfError] = useState(null);

    useEffect(() => {
      if (!pdfView) return;
      let cancelled = false;
      setPdfLoading(true); setBlobUrl(null); setPdfError(null);
      const cleanUrl = pdfView.url.startsWith('/api/') ? pdfView.url.slice(4) : pdfView.url;
      api.get(cleanUrl, { responseType: 'blob', validateStatus: s => s < 500 })
        .then(res => {
          if (cancelled) return;
          if (res.status >= 400) { setPdfError('Documento no encontrado'); setPdfLoading(false); return; }
          const blob = new Blob([res.data], { type: 'application/pdf' });
          setBlobUrl(URL.createObjectURL(blob));
          setPdfLoading(false);
        })
        .catch(() => { if (!cancelled) { setPdfError('Error de conexión'); setPdfLoading(false); } });
      return () => { cancelled = true; };
    }, []);

    useEffect(() => { return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }; }, [blobUrl]);

    return (
      <Dialog open onOpenChange={() => setPdfView(null)}>
        <DialogContent className="max-w-[900px] w-[90vw] h-[80vh] flex flex-col p-0 gap-0"
          onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader className="px-4 py-3 border-b pr-12 space-y-0">
            <DialogTitle className="text-sm font-semibold">{pdfView.title}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 p-2 min-h-0">
            {pdfLoading ? <div className="flex justify-center items-center h-full"><Loader2 className="w-8 h-8 animate-spin" /></div>
              : blobUrl ? <iframe src={blobUrl} className="w-full h-full border-none rounded" title={pdfView.title} />
              : <div className="text-center pt-16 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">{pdfError || 'Documento no disponible'}</p>
                </div>}
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div data-testid="seguimiento-oc-page" className="space-y-4 pb-20">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-blue-600" />
            Seguimiento Órdenes de Compra
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{total} orden(es) encontrada(s)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleSyncAlegra} disabled={syncing} data-testid="sync-alegra-btn">
            {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CloudDownload className="w-4 h-4 mr-1" />}
            {syncing ? 'Sincronizando...' : 'Sync Alegra'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setPage(0); fetchOrdenes(); fetchContabStats(); }}>
            <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={openRecurrentesModal}
            className="text-indigo-700 border-indigo-300 hover:bg-indigo-50"
            data-testid="btn-candidatas-recurrentes"
            title="Detectar OCs históricas que parecen recurrentes (mismo proveedor con ≥2 facturas asociadas)"
          >
            🔁 OCs recurrentes
          </Button>
          <Button variant="outline" size="sm" onClick={imprimirSeguimiento} title="Imprimir listado" data-testid="btn-print-seguimiento-oc">
            <Printer className="w-4 h-4 mr-1" /> Imprimir
          </Button>
          <Button variant="outline" size="sm" onClick={exportarSeguimientoExcel} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar a Excel" data-testid="btn-excel-seguimiento-oc">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="flex flex-wrap items-center gap-3 bg-slate-50 p-3 rounded-lg border" data-testid="date-range-filter">
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Período:</span>
        <div className="flex items-center gap-1.5">
          <label className="text-xs text-muted-foreground">Desde</label>
          <Input type="date" className="w-[150px] h-8 text-xs" value={fechaDesde}
            data-testid="fecha-desde"
            onChange={e => { setFechaDesde(e.target.value); setPage(0); }} />
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-xs text-muted-foreground">Hasta</label>
          <Input type="date" className="w-[150px] h-8 text-xs" value={fechaHasta}
            data-testid="fecha-hasta"
            onChange={e => { setFechaHasta(e.target.value); setPage(0); }} />
        </div>
        <div className="flex gap-1.5 ml-2">
          {[
            { label: '2026', desde: '2026-01-01', hasta: '' },
            { label: '2025', desde: '2025-01-01', hasta: '2025-12-31' },
            { label: '2024', desde: '2024-01-01', hasta: '2024-12-31' },
            { label: 'Todo', desde: 'all', hasta: 'all' },
          ].map(p => (
            <button key={p.label} data-testid={`period-${p.label.toLowerCase()}`}
              onClick={() => { setFechaDesde(p.desde); setFechaHasta(p.hasta); setPage(0); }}
              className={`text-[10px] px-2.5 py-1 rounded border font-medium transition-all ${
                fechaDesde === p.desde && fechaHasta === p.hasta
                  ? 'bg-slate-700 text-white border-slate-700'
                  : 'bg-white border-gray-200 hover:bg-slate-100'
              }`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Resumen Contabilización */}
      {contabStats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="contab-summary">
          <button onClick={() => { setEstadoContableFilter(''); setPage(0); }}
            className={`text-left p-3 rounded-lg border transition-all ${!estadoContableFilter ? 'ring-2 ring-blue-400 border-blue-300' : 'hover:border-gray-300'} bg-white`}>
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Total OCs</p>
            <p className="text-xl font-bold text-gray-900">{(contabStats.ocs_totales ?? 0).toLocaleString('es-CL')}</p>
          </button>
          <button onClick={() => { setEstadoContableFilter('total'); setPage(0); }}
            className={`text-left p-3 rounded-lg border transition-all ${estadoContableFilter === 'total' ? 'ring-2 ring-emerald-400 border-emerald-300' : 'hover:border-emerald-200'} bg-emerald-50/60`}>
            <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Contabilizadas</p>
            <p className="text-xl font-bold text-emerald-700">{contabStats.ocs_contab_total ?? 0}</p>
            <p className="text-[10px] text-emerald-600">{contabStats.ocs_totales ? ((contabStats.ocs_contab_total / contabStats.ocs_totales) * 100).toFixed(1) : 0}%</p>
          </button>
          <button onClick={() => { setEstadoContableFilter('parcial'); setPage(0); }}
            className={`text-left p-3 rounded-lg border transition-all ${estadoContableFilter === 'parcial' ? 'ring-2 ring-amber-400 border-amber-300' : 'hover:border-amber-200'} bg-amber-50/60`}>
            <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider flex items-center gap-1"><Clock className="w-3 h-3" /> Parcial</p>
            <p className="text-xl font-bold text-amber-700">{contabStats.ocs_contab_parcial ?? 0}</p>
            <p className="text-[10px] text-amber-600">Faltan facturas</p>
          </button>
          <button onClick={() => { setEstadoContableFilter('excede'); setPage(0); }}
            className={`text-left p-3 rounded-lg border transition-all ${estadoContableFilter === 'excede' ? 'ring-2 ring-purple-400 border-purple-300' : 'hover:border-purple-200'} bg-purple-50/60`}>
            <p className="text-[10px] font-semibold text-purple-700 uppercase tracking-wider flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Excede OC</p>
            <p className="text-xl font-bold text-purple-700">{contabStats.ocs_contab_excede ?? 0}</p>
            <p className="text-[10px] text-purple-600">Monto facturado mayor</p>
          </button>
          <button onClick={() => { setEstadoContableFilter('pendiente'); setPage(0); }}
            className={`text-left p-3 rounded-lg border transition-all ${estadoContableFilter === 'pendiente' ? 'ring-2 ring-red-400 border-red-300' : 'hover:border-red-200'} bg-red-50/60`}>
            <p className="text-[10px] font-semibold text-red-600 uppercase tracking-wider flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Sin Contabilizar</p>
            <p className="text-xl font-bold text-red-600">{(contabStats.ocs_sin_contabilizar ?? 0).toLocaleString('es-CL')}</p>
            <p className="text-[10px] text-red-500">Pendientes Alegra</p>
          </button>
        </div>
      )}

      {/* Search & Filters */}
      <Card>
        <CardContent className="p-3 flex flex-col gap-3">
          <form onSubmit={handleSearch} className="flex gap-2 flex-1">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="search-oc"
                placeholder="Buscar por N° OC, proveedor, RUT..."
                className="pl-9"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <Button type="submit" size="sm" data-testid="search-oc-btn">
              <Search className="w-4 h-4 mr-1" /> Buscar
            </Button>
          </form>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider shrink-0">Aprobación:</span>
              {ESTADOS_OC.map(e => (
                <button key={e.value} data-testid={`filter-${e.value || 'all'}`}
                  onClick={() => { setEstadoFilter(e.value); setPage(0); }}
                  className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-all ${
                    estadoFilter === e.value
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white border-gray-200 hover:bg-blue-50 hover:border-blue-300'
                  }`}>
                  {e.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider shrink-0">Contabilización:</span>
              {ESTADOS_CONTABLE.map(e => (
                <button key={e.value} data-testid={`filter-contable-${e.value || 'all'}`}
                  onClick={() => { setEstadoContableFilter(e.value); setPage(0); }}
                  className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-all ${
                    estadoContableFilter === e.value
                      ? e.value === 'total' ? 'bg-emerald-600 text-white border-emerald-600'
                        : e.value === 'parcial' ? 'bg-amber-500 text-white border-amber-500'
                        : e.value === 'excede' ? 'bg-purple-600 text-white border-purple-600'
                        : e.value === 'pendiente' ? 'bg-red-500 text-white border-red-500'
                        : 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                  }`}>
                  {e.label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <TableSkeleton rows={8} cols={7} />
          ) : ordenes.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Sin resultados</p>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[75vh] overflow-y-auto relative">
              <table className="w-full text-xs" data-testid="oc-table">
                <thead className="bg-white border-b sticky top-0 z-10 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                  <tr className="bg-muted/50">
                    <th className="px-3 py-2.5 text-center font-semibold">Origen</th>
                    <th className="px-3 py-2.5 text-left font-semibold">N OC</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Fecha</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Proveedor</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Neto</th>
                    <th className="px-3 py-2.5 text-right font-semibold">IVA</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Total</th>
                    <th className="px-3 py-2.5 text-center font-semibold">Estado</th>
                    <th className="px-3 py-2.5 text-center font-semibold">V°B° Pago</th>
                    <th className="px-3 py-2.5 text-center font-semibold">Contabilización</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Recepción</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Factura/DTE</th>
                    <th className="px-3 py-2.5 text-center font-semibold">Docs</th>
                    <th className="px-3 py-2.5 text-center font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {ordenes.map((oc, i) => (
                    <tr key={oc.numero_oc || i} className="border-b hover:bg-muted/30 transition-colors" data-testid={`oc-row-${oc.numero_oc}`}>
                      <td className="px-3 py-2 text-center">
                        {oc.source === 'web' ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                            <Globe className="w-3 h-3" /> Web
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-sky-100 text-sky-700 border border-sky-200">
                            <Database className="w-3 h-3" /> Access
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-semibold text-blue-700"><OCNumber oc={oc} size="sm" /></td>
                      <td className="px-3 py-2 text-muted-foreground">{fmtDate(oc.fecha)}</td>
                      <td className="px-3 py-2">
                        <div className="truncate max-w-[200px]">{oc.proveedor_nombre || oc.rut_proveedor || '-'}</div>
                        {oc.proveedor_nombre && oc.rut_proveedor && <span className="text-[10px] text-muted-foreground">{oc.rut_proveedor}</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">${fmt(oc.neto)}</td>
                      <td className="px-3 py-2 text-right font-mono text-muted-foreground">${fmt(oc.iva)}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold">${fmt(oc.total)}</td>
                      <td className="px-3 py-2 text-center">
                        <div className="inline-flex items-center gap-1">
                          {oc.aprobada ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full border border-green-200">
                              <CheckCircle className="w-3 h-3" /> Aprobada
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                              <Clock className="w-3 h-3" /> Pendiente
                            </span>
                          )}
                          {/* Iter 236.m.10 (Carlos): badge Recurrente para OCs que admiten múltiples facturas */}
                          {oc.es_recurrente && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-200"
                              title="Esta OC admite múltiples facturas (servicio recurrente mensual)"
                              data-testid={`badge-recurrente-${oc.numero_oc}`}
                            >
                              🔁 Recurrente
                            </span>
                          )}
                        </div>
                      </td>
                      {/* V°B° para Pago column (Iter 413) */}
                      <td className="px-3 py-2 text-center" data-testid={`vb-pago-status-${oc.numero_oc}`}>
                        <VbPagoBadge estado={oc.estado_vb_pago} numeroOc={oc.numero_oc} />
                      </td>
                      {/* Contabilización column */}
                      <td className="px-3 py-2 text-center" data-testid={`contab-status-${oc.numero_oc}`}>
                        {oc.estado_contabilizacion === 'total' ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                            <CheckCircle className="w-3 h-3" /> Contabilizada
                          </span>
                        ) : oc.estado_contabilizacion === 'parcial' ? (
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                              <Clock className="w-3 h-3" /> Parcial
                            </span>
                            {oc.monto_contabilizado > 0 && (
                              <span className="text-[9px] text-amber-600">${oc.monto_contabilizado?.toLocaleString('es-CL')}</span>
                            )}
                          </div>
                        ) : oc.estado_contabilizacion === 'excede' ? (
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-200">
                              <AlertTriangle className="w-3 h-3" /> Excede OC
                            </span>
                            {oc.monto_contabilizado > 0 && (
                              <span className="text-[9px] text-purple-600">${oc.monto_contabilizado?.toLocaleString('es-CL')}</span>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full border border-red-200">
                            <AlertTriangle className="w-3 h-3" /> Sin Contab.
                          </span>
                        )}
                      </td>
                      {/* Recepciones column */}
                      <td className="px-3 py-2">
                        {oc.tiene_recepcion ? (
                          <div className="space-y-0.5">
                            {(oc.recepciones || []).slice(0, 3).map((r, ri) => (
                              <button key={ri}
                                className="flex items-center gap-1 text-[10px] text-green-700 hover:text-green-900 hover:underline"
                                onClick={() => openPdf(`/compras/recepciones/${r.numero_recepcion}/pdf`, `Rec. ${r.numero_recepcion}`)}>
                                <Package className="w-3 h-3" />
                                <span className="font-medium">{r.numero_recepcion}</span>
                                {r.monto_total > 0 && <span className="text-muted-foreground ml-0.5">${fmt(r.monto_total)}</span>}
                              </button>
                            ))}
                            {(oc.recepciones || []).length > 3 && (
                              <span className="text-[9px] text-muted-foreground">+{oc.recepciones.length - 3} más</span>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-red-500 bg-red-50 px-1.5 py-0.5 rounded border border-red-200">
                            <AlertTriangle className="w-3 h-3" /> Sin recepción
                          </span>
                        )}
                      </td>
                      {/* Facturas/DTE column */}
                      <td className="px-3 py-2">
                        {oc.tiene_factura ? (
                          <div className="space-y-0.5">
                            {(oc.facturas_dte || []).slice(0, 3).map((f, fi) => (
                              <div key={fi} className="text-[10px]">
                                <span className="font-medium text-purple-700">DTE {f.folio || '-'}</span>
                                {f.monto_total && <span className="text-muted-foreground ml-1">${fmt(f.monto_total)}</span>}
                              </div>
                            ))}
                            {(oc.facturas_dte || []).length > 3 && (
                              <span className="text-[9px] text-muted-foreground">+{oc.facturas_dte.length - 3} más</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-[10px] text-muted-foreground italic">Sin DTE</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1">
                          {/* OC PDF */}
                          <button title="PDF OC" data-testid={`doc-oc-${oc.numero_oc}`}
                            onClick={() => openPdf(`/compras/oc/${oc.numero_oc}/pdf`, `OC ${oc.numero_oc}`)}
                            className="p-1 rounded hover:bg-blue-50 text-blue-600 transition-colors">
                            <FileText className="w-3.5 h-3.5" />
                          </button>
                          {/* Scan - only if has factura */}
                          {oc.tiene_factura && (oc.facturas_dte || []).length > 0 && (
                            <button title="Scan" data-testid={`doc-scan-${oc.numero_oc}`}
                              onClick={() => openScan(oc.facturas_dte[0].folio || '')}
                              className="p-1 rounded hover:bg-amber-50 text-amber-600 transition-colors">
                              <ScanLine className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="outline" size="sm" className="h-7 px-2 text-[10px]"
                            data-testid={`ver-oc-${oc.numero_oc}`}
                            onClick={() => openOCDetail(oc)}>
                            <Eye className="w-3 h-3 mr-0.5" /> Ver
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 px-2 text-[10px]"
                            data-testid={`print-oc-${oc.numero_oc}`}
                            onClick={() => handlePrint(oc)}>
                            <Printer className="w-3 h-3 mr-0.5" /> PDF
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            Página {page + 1} de {totalPages} ({total} resultados)
          </span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* OC Detail Dialog - Full OCViewer */}
      <Dialog open={!!ocDetail} onOpenChange={() => setOcDetail(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-0 gap-0" data-testid="oc-detail-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Package className="w-5 h-5 text-blue-600" />
              Orden de Compra {ocDetail?.oc?.numero_oc}
            </DialogTitle>
            <DialogDescription>Vista completa de la OC con detalle de items</DialogDescription>
          </DialogHeader>
          <div className="p-0">
            {ocDetail?.loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
            ) : ocDetail?.oc ? (
              <>
                <OCViewer
                  oc={ocDetail.oc}
                  detalle={ocDetail.detalle || []}
                  proveedor={ocDetail.proveedor || {}}
                  operaciones={ocDetail.operaciones || []}
                  formaPago={ocDetail.forma_pago || ''}
                  esTarjeta={ocDetail.es_tarjeta || false}
                  empresa={empresa}
                  editableSello={['admin', 'administrador', 'gerencia', 'compras', 'bodega', 'bodega_admin', 'jefe_bodega'].includes(role)}
                  editableVbPago={['admin', 'administrador', 'gerencia', 'compras', 'jefe_proyectos', 'jefatura_proyectos', 'jefe_proyecto'].includes(role)}
                  onVbPagoChange={handleVbPagoChange}
                />

                {/* Associated documents section */}
                <div className="px-5 py-3 border-t space-y-3">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Documentos Asociados</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Recepciones */}
                    <div className={`rounded-lg border p-3 ${
                      (ocDetail.oc?.recepciones || []).length > 0 ? 'bg-green-50/50 border-green-200' : 'bg-red-50/50 border-red-200'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Package className={`w-4 h-4 ${(ocDetail.oc?.recepciones || []).length > 0 ? 'text-green-600' : 'text-red-500'}`} />
                        <span className="text-xs font-semibold">Recepciones</span>
                        <span className="text-[10px] text-muted-foreground">({(ocDetail.oc?.recepciones || []).length})</span>
                      </div>
                      {(ocDetail.oc?.recepciones || []).length > 0 ? (
                        <div className="space-y-1">
                          {(ocDetail.oc.recepciones).map((r, i) => {
                            const anulada = !!r.anulada;
                            return (
                              <div key={`rec-${r.numero_recepcion}`}
                                className={`flex items-center gap-1 w-full text-xs rounded px-2 py-1 border transition-colors ${
                                  anulada
                                    ? 'bg-red-50 border-red-200 text-red-600'
                                    : 'bg-white hover:bg-green-50'
                                }`}>
                                <button
                                  type="button"
                                  className="flex-1 text-left flex items-center justify-between gap-2 disabled:opacity-60"
                                  onClick={() => openPdf(`/compras/recepciones/${r.numero_recepcion}/pdf`, `Rec. ${r.numero_recepcion}`)}
                                  data-testid={`btn-view-recepcion-${r.numero_recepcion}`}
                                >
                                  <span className={`font-medium ${anulada ? 'line-through text-red-700' : 'text-green-700'}`}>
                                    {r.numero_recepcion}
                                  </span>
                                  {anulada && (
                                    <span className="text-[9px] bg-red-100 text-red-700 border border-red-300 px-1 rounded font-semibold">
                                      ANULADA
                                    </span>
                                  )}
                                  <span className="text-[10px] text-muted-foreground">{fmtDate(r.fecha)}</span>
                                </button>
                                {canManageRecepciones && !anulada && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); abrirEditarRecepcion(r.numero_recepcion, ocDetail.oc.numero_oc); }}
                                      className="p-1 hover:bg-blue-100 rounded text-blue-600 transition-colors"
                                      title="Editar cantidades de la recepción"
                                      data-testid={`btn-edit-recepcion-${r.numero_recepcion}`}
                                    >
                                      <Pencil className="w-3 h-3" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); setAnularDlg({ numero_recepcion: r.numero_recepcion, numero_oc: ocDetail.oc.numero_oc }); setAnularMotivo(''); }}
                                      className="p-1 hover:bg-red-100 rounded text-red-600 transition-colors"
                                      title="Anular esta recepción (revertir stock + liberar OC)"
                                      data-testid={`btn-anular-recepcion-${r.numero_recepcion}`}
                                    >
                                      <Ban className="w-3 h-3" />
                                    </button>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-xs text-red-600">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          <span className="font-medium">Sin recepción registrada</span>
                        </div>
                      )}
                    </div>

                    {/* Facturas/DTEs */}
                    <div className={`rounded-lg border p-3 ${
                      (ocDetail.oc?.facturas_dte || []).length > 0 ? 'bg-purple-50/50 border-purple-200' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className={`w-4 h-4 ${(ocDetail.oc?.facturas_dte || []).length > 0 ? 'text-purple-600' : 'text-gray-400'}`} />
                        <span className="text-xs font-semibold">Facturas / DTE</span>
                        <span className="text-[10px] text-muted-foreground">({(ocDetail.oc?.facturas_dte || []).length})</span>
                      </div>
                      {(ocDetail.oc?.facturas_dte || []).length > 0 ? (
                        <div className="space-y-1">
                          {(ocDetail.oc.facturas_dte).map((f, i) => (
                            <div key={`dte-${f.folio || i}`} className="flex items-center justify-between text-xs bg-white rounded px-2 py-1 border">
                              <span className="font-medium text-purple-700">DTE {f.folio || '-'}</span>
                              <span className="text-muted-foreground">{f.razon_social ? f.razon_social.substring(0, 25) : ''}</span>
                              {f.monto_total && <span className="font-mono text-[10px]">${fmt(f.monto_total)}</span>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">Sin facturas/DTE asociadas</span>
                      )}
                    </div>

                    {/* Notas de Crédito aplicadas (NC proveedor → Nota de Débito Alegra) */}
                    {(ocDetail.oc?.ncs_aplicadas || []).length > 0 && (
                      <div className="rounded-lg border p-3 bg-orange-50/50 border-orange-200" data-testid="oc-ncs-aplicadas">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="w-4 h-4 text-orange-600" />
                          <span className="text-xs font-semibold">Notas de Crédito aplicadas</span>
                          <span className="text-[10px] text-muted-foreground">
                            (−${fmt(ocDetail.oc.ncs_aplicadas_monto_total || 0)})
                          </span>
                        </div>
                        <div className="space-y-1">
                          {(ocDetail.oc.ncs_aplicadas).map((nc, i) => (
                            <div key={`nc-${nc.folio_nc || i}`} className="flex items-center justify-between text-xs bg-white rounded px-2 py-1 border">
                              <span className="font-medium text-orange-700">NC {nc.folio_nc}</span>
                              <span className="text-muted-foreground">→ Fact {nc.factura_folio} → OC {ocDetail.oc.numero_oc}</span>
                              <span className="font-mono text-[10px] text-orange-700">−${fmt(nc.monto)}</span>
                            </div>
                          ))}
                        </div>
                        {ocDetail.oc.total_original != null && (
                          <div className="flex items-center justify-between text-xs mt-2 pt-2 border-t border-orange-200" data-testid="oc-total-ajustado">
                            <span className="text-muted-foreground">Total OC</span>
                            <span>
                              <span className="line-through text-muted-foreground mr-2">${fmt(ocDetail.oc.total_original)}</span>
                              <span className="font-bold text-emerald-700">${fmt(ocDetail.oc.total)}</span>
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="px-5 py-3 border-t flex gap-2 bg-muted/30">
                  <Button variant="outline" size="sm" data-testid="detail-pdf-oc"
                    onClick={() => openPdf(`/compras/oc/${ocDetail.oc.numero_oc}/pdf`, `OC ${ocDetail.oc.numero_oc}`)}>
                    <FileText className="w-4 h-4 mr-1 text-blue-600" /> PDF OC
                  </Button>
                  <Button size="sm" className="ml-auto bg-blue-600 hover:bg-blue-700 text-white" data-testid="detail-print"
                    onClick={() => handlePrint(ocDetail.oc)}>
                    <Printer className="w-4 h-4 mr-1" /> Imprimir PDF
                  </Button>
                </div>
              </>
            ) : (
              <div className="p-8 text-center text-muted-foreground">
                <Package className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>No se pudo cargar la OC</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Viewer Dialog */}
      {pdfView && <PdfViewerDialog />}

      {/* Iter 220: Dialog Anular Recepción */}
      <Dialog open={!!anularDlg} onOpenChange={(o) => { if (!o) { setAnularDlg(null); setAnularMotivo(''); } }}>
        <DialogContent className="max-w-md" data-testid="dialog-anular-recepcion">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Ban className="w-5 h-5" /> Anular Recepción
            </DialogTitle>
            <DialogDescription>
              Vas a anular <strong>{anularDlg?.numero_recepcion}</strong> de la OC <strong>{anularDlg?.numero_oc}</strong>.
              Esta acción <strong>revierte el stock</strong> sumado, libera la factura de la OC, y queda registrado en el log de auditoría.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
              ⚠ No se podrá anular si la factura asociada ya está contabilizada en Alegra.
            </div>
            <div>
              <Label htmlFor="motivo-anular" className="text-xs font-medium">Motivo de anulación (mín. 5 caracteres)</Label>
              <Textarea
                id="motivo-anular"
                value={anularMotivo}
                onChange={(e) => setAnularMotivo(e.target.value)}
                placeholder="Ej: factura emitida con cantidad incorrecta, devolución al proveedor, etc."
                className="mt-1 text-sm"
                rows={3}
                data-testid="input-motivo-anular"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnularDlg(null)} disabled={anulando}>Cancelar</Button>
            <Button
              variant="destructive"
              onClick={confirmarAnular}
              disabled={anulando || (anularMotivo || '').trim().length < 5}
              data-testid="btn-confirm-anular"
            >
              {anulando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}
              Anular Recepción
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 220: Dialog Editar Recepción */}
      <Dialog open={!!editDlg} onOpenChange={(o) => { if (!o) setEditDlg(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-editar-recepcion">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Pencil className="w-5 h-5" /> Editar Recepción {editDlg?.numero_recepcion}
            </DialogTitle>
            <DialogDescription>
              Modificá cantidades y valores. El sistema ajustará automáticamente el stock (+/-) y recalculará el monto total.
            </DialogDescription>
          </DialogHeader>
          {editDlg && (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
                ⚠ No se podrá guardar si la factura asociada ya está contabilizada en Alegra.
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted text-left">
                    <tr>
                      <th className="px-2 py-1.5 w-10">#</th>
                      <th className="px-2 py-1.5">Descripción</th>
                      <th className="px-2 py-1.5 w-28">Cant. recep.</th>
                      <th className="px-2 py-1.5 w-32">Valor unit.</th>
                      <th className="px-2 py-1.5 w-28 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editDlg.items.map((it, idx) => {
                      const total = (Number(it.cantidad_recepcionada) || 0) * (Number(it.valor_unitario) || 0);
                      return (
                        <tr key={`edit-${idx}`} className="border-t">
                          <td className="px-2 py-1.5">{it.item}</td>
                          <td className="px-2 py-1.5">
                            <span className="block truncate max-w-xs">{it.descripcion}</span>
                            {it.codigo_material && <span className="text-[10px] text-muted-foreground font-mono">{it.codigo_material}</span>}
                          </td>
                          <td className="px-2 py-1.5">
                            <Input
                              type="number" step="0.01" min="0"
                              value={it.cantidad_recepcionada}
                              onChange={(e) => {
                                const next = [...editDlg.items];
                                next[idx] = { ...it, cantidad_recepcionada: e.target.value };
                                setEditDlg({ ...editDlg, items: next });
                              }}
                              className="h-8 text-xs"
                              data-testid={`input-cant-${idx}`}
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <Input
                              type="number" step="0.01" min="0"
                              value={it.valor_unitario}
                              onChange={(e) => {
                                const next = [...editDlg.items];
                                next[idx] = { ...it, valor_unitario: e.target.value };
                                setEditDlg({ ...editDlg, items: next });
                              }}
                              className="h-8 text-xs"
                              data-testid={`input-vu-${idx}`}
                            />
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono">${fmt(Math.round(total))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t font-bold bg-muted/30">
                      <td colSpan={4} className="px-2 py-2 text-right">Total recepción</td>
                      <td className="px-2 py-2 text-right font-mono">
                        ${fmt(Math.round(editDlg.items.reduce((s, it) => s + ((Number(it.cantidad_recepcionada) || 0) * (Number(it.valor_unitario) || 0)), 0)))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div>
                <Label htmlFor="motivo-edit" className="text-xs font-medium">Motivo del cambio (opcional)</Label>
                <Input
                  id="motivo-edit"
                  value={editDlg.motivo}
                  onChange={(e) => setEditDlg({ ...editDlg, motivo: e.target.value })}
                  placeholder="Ej: corrección de cantidad por error de digitación"
                  className="mt-1 text-sm"
                  data-testid="input-motivo-editar"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDlg(null)} disabled={editing}>Cancelar</Button>
            <Button onClick={confirmarEditar} disabled={editing} data-testid="btn-confirm-editar">
              {editing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Pencil className="w-4 h-4 mr-1" />}
              Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 236.m.10 (Carlos): Modal Candidatas a OC Recurrente */}
      <Dialog open={recurrentesModal} onOpenChange={setRecurrentesModal}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-700">
              🔁 OCs candidatas a Recurrente
            </DialogTitle>
            <DialogDescription>
              OCs históricas con ≥2 facturas asociadas — típico de servicios mensuales
              (arriendo, internet, vigilancia, electricidad). Marcalas como{' '}
              <b>recurrentes</b> para permitir asociar más facturas sin bloqueo.
            </DialogDescription>
          </DialogHeader>
          {recurrentesLoading ? (
            <div className="py-8 text-center text-muted-foreground">
              <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
              Analizando OCs…
            </div>
          ) : candidatas.length === 0 ? (
            <div className="py-8 text-center text-emerald-700 text-sm">
              ✓ No hay candidatas pendientes. Todas las OCs con múltiples facturas ya están marcadas.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tbl-candidatas-recurrentes">
                <thead className="bg-slate-100 sticky top-0">
                  <tr className="text-left">
                    <th className="px-2 py-1.5">OC</th>
                    <th className="px-2 py-1.5">Proveedor</th>
                    <th className="px-2 py-1.5 text-right">Monto OC</th>
                    <th className="px-2 py-1.5 text-center">Facturas</th>
                    <th className="px-2 py-1.5 text-right">Total facturado</th>
                    <th className="px-2 py-1.5 text-right">Sobre/Asig</th>
                    <th className="px-2 py-1.5 text-center">Confianza</th>
                    <th className="px-2 py-1.5 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {candidatas.map((c) => (
                    <tr key={c.numero_oc} className="border-b hover:bg-indigo-50/40" data-testid={`row-candidata-${c.numero_oc}`}>
                      <td className="px-2 py-1.5 font-mono font-semibold text-blue-700">{c.numero_oc}</td>
                      <td className="px-2 py-1.5">
                        <div className="truncate max-w-[220px]" title={c.nombre_proveedor}>{c.nombre_proveedor}</div>
                        <span className="text-[10px] text-muted-foreground font-mono">{c.rut_proveedor}</span>
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono">${fmt(c.monto_oc)}</td>
                      <td className="px-2 py-1.5 text-center font-bold">{c.facturas_asociadas}</td>
                      <td className="px-2 py-1.5 text-right font-mono">${fmt(c.monto_total_facturado)}</td>
                      <td className={`px-2 py-1.5 text-right font-mono ${c.sobreasignacion > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                        {c.sobreasignacion > 0 ? '+' : ''}${fmt(c.sobreasignacion)}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                          c.confianza === 'alta' ? 'bg-rose-100 text-rose-700' :
                          c.confianza === 'media' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>{c.confianza}</span>
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <Button
                          size="sm" variant="outline"
                          onClick={() => marcarComoRecurrente(c.numero_oc)}
                          disabled={marcandoRecurrente === c.numero_oc}
                          className="h-7 text-xs text-indigo-700 border-indigo-300 hover:bg-indigo-100"
                          data-testid={`btn-marcar-recurrente-${c.numero_oc}`}
                        >
                          {marcandoRecurrente === c.numero_oc ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <>🔁 Marcar</>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecurrentesModal(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
